import React, { useState, useEffect } from 'react';
import { X, Share, Plus, MoreVertical, Download, Smartphone, Monitor, ChevronRight } from 'lucide-react';

function getPlatform() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true;
  
  if (isStandalone) return 'standalone';
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
}

export default function AddToHomeScreenPopup() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('desktop');

  useEffect(() => {
    const alreadyShown = localStorage.getItem('pwa_install_prompt_shown');
    const detectedPlatform = getPlatform();
    setPlatform(detectedPlatform);

    if (!alreadyShown && detectedPlatform !== 'standalone') {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem('pwa_install_prompt_shown', 'true');
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      
      {/* Modal */}
      <div className="relative bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 pt-6 pb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl shadow-lg shrink-0 overflow-hidden">
              <img src="/apple-touch-icon.png" alt="YaDela" className="w-full h-full object-cover" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Установите YaDela</h2>
              <p className="text-white/80 text-sm mt-0.5">Быстрый доступ с домашнего экрана</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {platform === 'ios' && <IOSInstructions />}
          {platform === 'android' && <AndroidInstructions />}
          {platform === 'desktop' && <DesktopInstructions />}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-2.5 px-4 text-sm font-medium text-muted-foreground bg-muted hover:bg-muted/80 rounded-xl transition-colors"
          >
            Позже
          </button>
          <button
            onClick={handleClose}
            className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-colors"
          >
            Понятно!
          </button>
        </div>
      </div>
    </div>
  );
}

function StepItem({ number, icon: Icon, iconClassName, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
        {number}
      </div>
      <div className="flex-1 text-sm text-foreground leading-relaxed">
        <div className="flex items-center gap-1.5 flex-wrap">
          {children}
          {Icon && (
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${iconClassName || 'bg-muted'}`}>
              <Icon className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function IOSInstructions() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Safari на iPhone / iPad</span>
      </div>
      <StepItem number={1} icon={Share} iconClassName="bg-blue-100 dark:bg-blue-900/30 text-blue-500">
        Нажмите на кнопку «Поделиться»
      </StepItem>
      <StepItem number={2} icon={Plus} iconClassName="bg-muted text-foreground">
        Выберите «На экран Домой»
      </StepItem>
      <StepItem number={3}>
        <span>Нажмите <strong>«Добавить»</strong> — готово!</span>
      </StepItem>
    </div>
  );
}

function AndroidInstructions() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Chrome на Android</span>
      </div>
      <StepItem number={1} icon={MoreVertical} iconClassName="bg-muted text-foreground">
        Нажмите на меню
      </StepItem>
      <StepItem number={2} icon={Download} iconClassName="bg-green-100 dark:bg-green-900/30 text-green-600">
        Выберите «Установить приложение»
      </StepItem>
      <StepItem number={3}>
        <span>Подтвердите <strong>«Установить»</strong> — готово!</span>
      </StepItem>
    </div>
  );
}

function DesktopInstructions() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <Monitor className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Браузер на компьютере</span>
      </div>
      <StepItem number={1}>
        <span>В адресной строке найдите иконку</span>
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-muted">
          <Download className="w-3.5 h-3.5" />
        </span>
      </StepItem>
      <StepItem number={2}>
        <span>Или откройте меню браузера</span>
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-muted">
          <MoreVertical className="w-3.5 h-3.5" />
        </span>
      </StepItem>
      <StepItem number={3}>
        <span>Выберите <strong>«Установить приложение»</strong></span>
      </StepItem>
    </div>
  );
}
